"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

// Google Ads conversion id + PostHog public key (shared CodeFirst EU project).
const GTAG_ID = "AW-18199893944";
const PH_KEY = "phc_wet5bhEymPYyQy26QGPTdRUjyDuWm2UgWtpcQVbkweFF";
const CONSENT_KEY = "pa_consent_v1";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    posthog?: { capture: (e: string, p?: Record<string, unknown>) => void; opt_in_capturing: () => void; opt_out_capturing: () => void };
    paConversion?: (sendTo: string, value?: number) => void;
    paTrack?: (event: string, props?: Record<string, unknown>) => void;
    veResetConsent?: () => void;
  }
}

// Consent Mode v2 defaults (denied) — set as early as possible.
const CONSENT_DEFAULTS = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){ dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
    analytics_storage: "denied", wait_for_update: 500
  });
  gtag("js", new Date());
  gtag("config", "${GTAG_ID}");
`;

// PostHog loader (routes ingestion through this origin's /ingest proxy so
// tracker blockers can't silently drop events). Opted out until consent.
const PH_SNIPPET = `
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once unregister identify alias set_config people.set people.set_once people.unset reset opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_distinct_id get_session_id".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  posthog.init("${PH_KEY}", { api_host: location.origin + "/ingest", ui_host: "https://eu.posthog.com", persistence: "memory", autocapture: true });
  posthog.opt_out_capturing && posthog.opt_out_capturing();
  window.paTrack = function(ev, props){ try { window.posthog && window.posthog.capture(ev, props || {}); } catch(e){} };
  window.paConversion = function(sendTo, value){ try { window.gtag && window.gtag("event", "conversion", { send_to: sendTo, value: value || 0, currency: "GBP" }); } catch(e){} };
`;

export function Analytics() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (saved === "granted") grant();
    else if (saved !== "denied") setShow(true);
    window.veResetConsent = () => {
      localStorage.removeItem(CONSENT_KEY);
      setShow(true);
    };
  }, []);

  function grant() {
    window.gtag?.("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
    window.posthog?.opt_in_capturing?.();
  }

  return (
    <>
      <Script id="consent-defaults" strategy="beforeInteractive">{CONSENT_DEFAULTS}</Script>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`} strategy="afterInteractive" />
      <Script id="posthog-init" strategy="afterInteractive">{PH_SNIPPET}</Script>

      {show && (
        <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-line bg-white/95 px-5 py-4 shadow-[0_-8px_30px_rgba(16,24,32,.12)] backdrop-blur">
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[13px] text-slate">
              We use cookies to measure traffic and ad performance. You can accept or decline analytics &amp; advertising cookies.
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                className="rounded-[8px] border border-line bg-white px-4 py-2 text-[13px] font-bold text-ink hover:border-[#bdc8d1]"
                onClick={() => { localStorage.setItem(CONSENT_KEY, "denied"); setShow(false); }}
              >
                Decline
              </button>
              <button
                className="rounded-[8px] bg-action px-4 py-2 text-[13px] font-bold text-white hover:bg-action-dark"
                onClick={() => { localStorage.setItem(CONSENT_KEY, "granted"); grant(); setShow(false); }}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
